/*
 * The tenant fixtures that used to live here are gone.
 *
 * They were two invented companies -- "ABC Manufacturing" and "Harborline Logistics" -- shown in
 * a switcher above the page. Harmless while the pages were fixtures too, and the worst kind of
 * bug the moment they were not: a real tenant's real findings captioned with a company that does
 * not exist. The shell now takes the workspace name from the session.
 *
 * A switcher returns when a session can belong to several workspaces, and it will be a server
 * round trip rather than local state: letting the client name a workspace is asking the caller
 * which tenant's data to hand back.
 */

export interface NavItem {
  label: string
  path: string
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

/**
 * A rail entry: either a destination in its own right, or a group that opens one.
 *
 * BOTH SHAPES, because forcing everything into a group produced two headings that existed only
 * to satisfy the type. "Overview" wrapped Dashboard and nothing else, and "Account" lumped
 * Reports, Settings and Plans together although a report is a thing the product produces, a
 * setting is how it is configured, and a plan is what is being paid for. Once the groups opened
 * as flyouts both got worse in the same way: hovering a heading to reveal the single link behind
 * it, or three unrelated links behind one word.
 */
export type NavEntry = NavItem | NavGroup

export function isGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry
}

/** The sidebar, top to bottom. */
export const NAV_GROUPS: NavEntry[] = [
  // A destination, not a heading. It is the page people come back to.
  { label: 'Dashboard', path: '/dashboard' },

  // A destination, not a heading. "Recommendations" is the only Findings view the pilot sells.
  //
  // "SOC / Incidents" was removed for the pilot on purpose: Defenssive is a READ-ONLY Microsoft 365
  // baseline assessment, not a SOC and not an incident-response service. A nav entry promising one is
  // a claim the product must not make -- and it routed to a placeholder that said "not built", which
  // is worse than the entry being absent. It is not hidden behind a flag; it is not sold.
  { label: 'Recommendations', path: '/recommendations' },

  {
    label: 'Posture',
    items: [
      { label: 'Identity', path: '/identity' },
      { label: 'Endpoint', path: '/endpoint' },

      // Email arrived last and is the first area not read from Microsoft Graph. Eleven CISA
      // controls across Exchange Online and Defender for Office, collected over the endpoint the
      // ExchangeOnlineManagement PowerShell module itself calls.
      { label: 'Email', path: '/email' },

      // Data has findings and had nowhere to show them: external sharing could only be reached
      // from Recommendations, which lists every area at once.
      { label: 'Data', path: '/data' },

      // "Cloud" was removed (2026-09-24): the product did not assess cloud infrastructure, and a
      // posture page called Cloud read as a claim that Azure / cloud workloads are assessed. It comes
      // back with Azure CSPM (2026-10-10), and only where Azure is actually assessed: CLOUD_ITEM,
      // added by navGroupsFor when the server says so (Me.assessesAzure). Elsewhere /cloud still
      // redirects.

      // "Compliance" was removed for the pilot: Defenssive maps findings to CISA SCuBA and cites
      // framework references, but it is not a compliance-certification product and must not carry a
      // nav entry that reads as one. It, too, only ever routed to a "not built" placeholder.
    ],
  },

  // The three that used to share the word "Account". Each is its own errand.
  { label: 'Reports', path: '/reports' },
  { label: 'Settings', path: '/settings' },
  { label: 'Plans', path: '/plans' },
]

/** Cloud (Azure), drawn after Data only where this build assesses Azure. */
export const CLOUD_ITEM: NavItem = { label: 'Cloud', path: '/cloud' }

/** AI (Azure AI resources, agents and AI applications), drawn after Cloud where this build has the AI area. */
export const AI_ITEM: NavItem = { label: 'AI', path: '/ai' }

/**
 * AI assets (owner, 2026-10-12): every AI asset in the tenant on one page, a destination of its own after
 * Recommendations, where the AI area is on.
 */
export const AI_ASSETS_ITEM: NavItem = { label: 'AI assets', path: '/ai-assets' }

/** The sidebar for this session: NAV_GROUPS, with Cloud where Azure is assessed and AI where the AI area is on. */
export function navGroupsFor(assessesAzure: boolean, assessesAi = false): NavEntry[] {
  if (!assessesAzure && !assessesAi) return NAV_GROUPS
  const extra = [...(assessesAzure ? [CLOUD_ITEM] : []), ...(assessesAi ? [AI_ITEM] : [])]
  const groups = NAV_GROUPS.map((entry) => {
    if (!isGroup(entry) || !entry.items.some((i) => i.path === '/data')) return entry
    const at = entry.items.findIndex((i) => i.path === '/data') + 1
    return { ...entry, items: [...entry.items.slice(0, at), ...extra, ...entry.items.slice(at)] }
  })
  if (!assessesAi) return groups
  const after = groups.findIndex((entry) => !isGroup(entry) && entry.path === '/recommendations') + 1
  return [...groups.slice(0, after), AI_ASSETS_ITEM, ...groups.slice(after)]
}

/**
 * Every destination, flat.
 *
 * So routing and the breadcrumb do not each have to know that a rail entry has two shapes --
 * both used to flatMap over `.items`, which now silently skips every direct link, and a route
 * that quietly does not exist is a blank page rather than an error.
 */
export const NAV_DESTINATIONS: NavItem[] = NAV_GROUPS.flatMap((entry) =>
  isGroup(entry) ? entry.items : [entry],
)
